import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiFetch, parseJsonResponse, isNetworkError, isAuthError, isNetworkErrorResponse } from '../utils/api';

interface User {
  id: number;
  email: string;
  username: string;
  role: string;
  client_id: number | null;
  establishment_id: number | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const lastAuthCheckRef = React.useRef<number>(0);
  const tokenRef = React.useRef<string | null>(null); // Ref para evitar recreaciones de checkAuth
  const AUTH_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutos mínimo entre verificaciones automáticas

  // Sincronizar ref con state
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const checkAuth = useCallback(async (tokenToCheck?: string, forceCheck: boolean = false) => {
    const tokenValue = tokenToCheck || tokenRef.current;
    if (!tokenValue) {
      setLoading(false);
      return;
    }

    // Si no es una verificación forzada, respetar el intervalo mínimo
    if (!forceCheck) {
      const now = Date.now();
      const timeSinceLastCheck = now - lastAuthCheckRef.current;
      if (lastAuthCheckRef.current > 0 && timeSinceLastCheck < AUTH_CHECK_INTERVAL) {
        // No verificar tan frecuentemente - solo si pasó el intervalo
        console.log(`Verificación de autenticación omitida. Tiempo transcurrido: ${Math.round(timeSinceLastCheck / 1000)}s`);
        return;
      }
      lastAuthCheckRef.current = now;
    }

    try {
      const res = await apiFetch('auth/me', {}, tokenValue);
      
      // Si es error de red, mantener sesión y salir (NO reintentar automáticamente)
      if (isNetworkErrorResponse(res)) {
        console.warn('Error de red al verificar autenticación. Manteniendo sesión.');
        setLoading(false);
        // NO actualizar lastAuthCheck para evitar verificaciones constantes
        return;
      }

      const json = await parseJsonResponse(res);

      // Verificar si es error de autenticación (401) - SOLO en este caso cerrar sesión
      if (isAuthError(res.status, json)) {
        // Token inválido o expirado - cerrar sesión definitivamente
        console.warn('Error de autenticación detectado. Token inválido o expirado. Cerrando sesión.', { status: res.status });
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setHasInitialized(false);
        lastAuthCheckRef.current = 0;
        setLoading(false);
        return;
      }

      // Si la respuesta es exitosa y tiene datos
      if (res.ok && json.data && !json.data.error) {
        setUser(json.data.user);
        // Solo actualizar token si es diferente para evitar bucles
        if (tokenRef.current !== tokenValue) {
          setToken(tokenValue);
          tokenRef.current = tokenValue;
        }
        lastAuthCheckRef.current = Date.now();
        // Si el token está en localStorage pero no coincide, actualizarlo
        const savedToken = localStorage.getItem('token');
        if (savedToken !== tokenValue) {
          localStorage.setItem('token', tokenValue);
        }
      } else {
        // Otros errores HTTP (500, etc.) - mantener sesión pero loggear
        // IMPORTANTE: No cerrar sesión por errores que no sean 401
        console.error('Error verificando autenticación (no 401):', json);
        // Mantener la sesión actual - NO hacer nada más
        // NO actualizar lastAuthCheck para evitar verificaciones constantes en errores
      }
    } catch (error: any) {
      // Error de red o parsing - NO cerrar sesión
      if (isNetworkError(error)) {
        console.warn('Error de red verificando autenticación. Manteniendo sesión activa.');
        // Mantener la sesión actual - NO hacer nada
        // NO actualizar lastAuthCheck
      } else {
        console.error('Error inesperado verificando autenticación:', error);
        // Mantener la sesión actual incluso en errores inesperados
        // NO actualizar lastAuthCheck
      }
    } finally {
      setLoading(false);
      if (!hasInitialized) {
        setHasInitialized(true);
      }
    }
  }, []); // Array vacío - usar tokenRef en lugar de token para evitar recreaciones

  // Cargar token del localStorage SOLO AL INICIAR (una vez)
  useEffect(() => {
    // Solo ejecutar una vez al montar el componente
    // Protección contra React Strict Mode que ejecuta efectos dos veces
    if (hasInitialized) {
      return;
    }

    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      setToken(savedToken);
      tokenRef.current = savedToken;
      // Verificar inmediatamente al cargar (forceCheck = true para saltar el intervalo)
      // Usar setTimeout para evitar ejecución inmediata que pueda causar bucles
      setTimeout(() => {
        checkAuth(savedToken, true);
      }, 0);
    } else {
      setLoading(false);
      setHasInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vacío = solo ejecutar al montar

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      console.log('🔐 [Login] Iniciando login para:', email);
      const res = await apiFetch('auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });

      console.log('📥 [Login] Respuesta recibida:', {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries())
      });

      // Si es error de red, lanzar error específico
      if (isNetworkErrorResponse(res)) {
        console.error('❌ [Login] Error de red detectado');
        throw new Error('Error de conexión. Verifica tu conexión a internet.');
      }

      const json = await parseJsonResponse(res);
      console.log('📦 [Login] JSON parseado:', {
        success: json.success,
        hasError: !!json.error,
        hasData: !!json.data,
        dataKeys: json.data ? Object.keys(json.data) : [],
        fullJson: json
      });

      if (!res.ok || json.error || !json.data?.token) {
        console.error('❌ [Login] Error en respuesta:', {
          resOk: res.ok,
          jsonError: json.error,
          hasToken: !!json.data?.token,
          jsonData: json.data,
          jsonMessage: json.message
        });
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error de autenticación';
        throw new Error(errorMessage);
      }

      console.log('✅ [Login] Login exitoso, guardando datos...');
      const data = json.data;
      console.log('📝 [Login] Datos a guardar:', {
        hasToken: !!data.token,
        hasUser: !!data.user,
        userRole: data.user?.role,
        userId: data.user?.id
      });
      
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      console.log('✅ [Login] Datos guardados correctamente');
    } catch (error: any) {
      console.error('❌ [Login] Error capturado:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      // Solo limpiar si no es error de red
      if (!isNetworkError(error)) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await apiFetch('auth/logout', {}, token);
      }
    } catch (error) {
      // No importa si falla el logout en el servidor, siempre limpiar localmente
      console.error('Error al cerrar sesión en el servidor:', error);
    } finally {
      // Siempre limpiar la sesión local, incluso si falla la llamada al servidor
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const isAuthenticated = !!user && !!token;
  
  // Log para debugging (solo en desarrollo y con throttling)
  useEffect(() => {
    // Solo loggear en desarrollo y evitar logs excesivos
    if (process.env.NODE_ENV === 'development') {
      const logTimeout = setTimeout(() => {
        console.log('🔐 [AuthContext] Estado de autenticación actualizado:', {
          hasUser: !!user,
          hasToken: !!token,
          isAuthenticated,
          userRole: user?.role,
          userId: user?.id
        });
      }, 100); // Throttle de 100ms para reducir logs
      
      return () => clearTimeout(logTimeout);
    }
  }, [user, token, isAuthenticated]);

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    checkAuth: () => checkAuth(undefined, true), // Permitir verificación manual forzada
    isAuthenticated
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

